import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type RouteLocationNormalized,
} from 'vue-router'
import authService from './api/authService'
import { getInstance as getAuth0ServiceIntance } from '@/auth/index'
import {
  isInMaintenance,
  getDomainUnderMaintenance,
} from '@/plan/metadata/metadata.helper'
import { getAppMetadata } from './plan/metadata/metadata'
import {
  StorageDestination,
  setValueToStorage,
  getValueFromStorage,
  removeValueFromStorage,
} from '@/cache/storage.helper'
import { SessionStorageKeys } from './constants'
import SharedPlanPasswordManager from '@/helpers/sharedPlanPassword.helper'

declare module 'vue-router' {
  interface RouteMeta {
    skipAuth?: boolean
    toolbar?: boolean
  }
}

const Root = () => import('./views/root/Root.vue')
const Login = () => import('./views/login/Login.vue')
const Dashboard = () => import('./views/dashboard/Dashboard.vue')
const Plan = () => import('./views/plan/Plan.vue')
const SharedPlanFeedback = () =>
  import('./views/shared-plan-feedback/SharedPlanFeedback.vue')
const SharedPlanPassword = () =>
  import('./views/shared-plan-password/SharedPlanPassword.vue')
const Maintenance = () => import('./views/maintenance/Maintenance.vue')
const FeaturedPackages = () =>
  import('./views/featured-packages/FeaturedPackages.vue')
const FeaturedPackagesCatalog = () =>
  import('./views/featured-packages-catalog/FeaturedPackagesCatalog.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/shared-plan-feedback',
    name: 'shared-plan-feedback',
    component: SharedPlanFeedback,
    meta: { skipAuth: true },
  },
  {
    path: '/shared-plan-password',
    name: 'shared-plan-password',
    component: SharedPlanPassword,
    meta: { skipAuth: true },
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: Maintenance,
    meta: { skipAuth: true },
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: { skipAuth: true },
  },
  {
    path: '/',
    component: Root,
    children: [
      {
        path: '',
        name: 'dashboard',
        component: Dashboard,
      },
      {
        path: 'pub',
        name: 'public',
        props: true,
        component: Plan,
        meta: { toolbar: true },
      },
      {
        path: 'plan/:id?',
        name: 'plan',
        props: true,
        component: Plan,
        meta: { toolbar: true },
      },
      {
        path: 'plan',
        name: 'share-plan',
        component: Plan,
        props: true,
        meta: { toolbar: true },
      },
      {
        path: 'featured-package',
        name: 'featured-package',
        props: true,
        component: Plan,
        meta: { toolbar: true },
        beforeEnter: (_to, from) => {
          if (!from.name) {
            return { name: 'featured-packages' }
          }
          return true
        },
      },
      {
        path: 'featured-packages',
        name: 'featured-packages',
        component: FeaturedPackages,
      },
      {
        path: 'featured-packages-catalog',
        name: 'featured-packages-catalog',
        component: FeaturedPackagesCatalog,
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

const redirectToMaintenance = (to: RouteLocationNormalized) => {
  const appMetadata = getAppMetadata()
  const needsRedirect = isInMaintenance(appMetadata)
  if (needsRedirect) {
    if (to.name !== 'maintenance') {
      const currentHost = window.location.host
      const domainsUnderMaintenance = getDomainUnderMaintenance(appMetadata)
      if (
        domainsUnderMaintenance.length === 0 ||
        domainsUnderMaintenance.some((domain) => currentHost.includes(domain))
      ) {
        router.push({ name: 'maintenance' })
      }
    }
  }
}

const needsPasswordRequest = (to: RouteLocationNormalized) => {
  const paramsTo = to.query
  const planId = String(paramsTo.planId)
  const sharedPasswordManager = SharedPlanPasswordManager.getInstance()
  const passwordIsRequired = sharedPasswordManager.isPasswordRequired()

  if (passwordIsRequired && sessionStorage.getItem(planId) === null) {
    router.push({
      name: 'shared-plan-password',
      query: to.query,
    })
  }
}

const isUrlForSharedPlan = (to: RouteLocationNormalized) => {
  const params = to.query
  const isPathValid = to.path === '/plan'
  const sharedPlan = params.shared === 'true' && isPathValid
  return sharedPlan
}

const isSharedPlanByUrlParams = () => {
  const url = window.location.href
  const queryParams = new URLSearchParams(url)
  const sharedPlan = queryParams.get('shared')
  return sharedPlan === 'true'
}

const isPasswordActionNeeded = () => {
  const sharedPasswordManager = SharedPlanPasswordManager.getInstance()
  const isPasswordRequired = sharedPasswordManager.isPasswordRequired()
  let isPasswordSet = false
  try {
    const password = sharedPasswordManager.getPassword()
    isPasswordSet = password !== undefined
  } catch (error) {
    // console.debug('🟨🟨🟨 Router: No password available')
    isPasswordSet = false
  }
  return isPasswordRequired && !isPasswordSet
}

const isSharedPlanActive = () => {
  const sharedPasswordManager = SharedPlanPasswordManager.getInstance()
  const isActive = sharedPasswordManager.isActive()

  return isActive
}

router.beforeEach((to) => {
  redirectToMaintenance(to)
  const sharedPlan = isUrlForSharedPlan(to)
  const auth0Service = getAuth0ServiceIntance()
  let needsAuth = !to.matched.some((m) => m.meta.skipAuth)
  if (sharedPlan) {
    needsAuth = false
    if (isSharedPlanActive()) {
      if (isPasswordActionNeeded()) {
        // needsPasswordRequest() pushes to the password route as a side effect;
        // fall through to allow the current navigation (legacy behaviour).
        needsPasswordRequest(to)
      } else {
        return true
      }
    } else {
      const origin = window.location.origin
      // vue-router 4: redirect from a guard by returning the target location.
      return {
        name: 'shared-plan-feedback',
        query: { task: 'error', link: origin + to.fullPath },
      }
    }
  }
  const hasAuth = auth0Service?.isUserAuthenticated || authService.isLoggedIn()
  const scopes = authService.getScopes() as string[]
  const isSharedPlan = isSharedPlanByUrlParams()
  const redirection = { name: 'login', query: { ...to.query, next: to.path } }

  const needsToReAuthenticate = needsAuth && !hasAuth
  if (needsToReAuthenticate) {
    setValueToStorage(
      StorageDestination.LocalStorage,
      SessionStorageKeys.PlanUrl,
      to.path,
    )
    return '/login?next=' + to.path
  } else if (needsAuth) {
    if (to.name === 'featured-packages') {
      const isAdmin = authService.isAdmin()
      if (!isAdmin) {
        return redirection
      }
    }
    if (hasAuth) {
      if (isSharedPlan) {
        const id = String(to.params.planid || to.params.id || to.params.planId)
        const allowedPlanIds = scopes
          .map((s) => s.split(':'))
          .filter((s) => s[0] === 'plan')
          .map((s) => s[1])

        const validPlanId = allowedPlanIds.indexOf(id) !== -1
        let allowed = false
        if (to.name === 'public') {
          allowed = true
        }
        if (to.name === 'plan' && validPlanId) {
          allowed = true
        }

        return allowed ? true : redirection
      } else {
        const urlOfPlanToredirect = getValueFromStorage(
          StorageDestination.LocalStorage,
          SessionStorageKeys.PlanUrl,
        )
        if (urlOfPlanToredirect) {
          removeValueFromStorage(
            StorageDestination.LocalStorage,
            SessionStorageKeys.PlanUrl,
          )
          return urlOfPlanToredirect
        }
        return true
      }
    } else {
      return redirection
    }
  }
  return true
})

export default router
